import { fetchBaseQuery, type BaseQueryFn, type FetchArgs, type FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { getStoredAccessToken } from '@/lib/auth'
import { extractApiErrorMessage, extractApiMessage } from '@/lib/api-message'
import { showApiErrorToast, showApiSuccessToast } from '@/lib/api-toast'
import { getFingerprintIdAsync } from '@/lib/fingerprint'
import { getPlayerApiBaseUrl } from '@/lib/player-api'
import { completeUsernameGate, waitForUsernameCompletion } from '@/lib/username-gate'
import { logout, setUsernameRequired } from '@/store/slices/authSlice'

const rawBaseQuery = fetchBaseQuery({
	baseUrl: getPlayerApiBaseUrl(),
})

function isUsernameRequiredError(result: { error?: FetchBaseQueryError }): boolean {
	return result.error && result.error?.status === 428
}

export const playerBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
	const fpid = await getFingerprintIdAsync()
	const token = getStoredAccessToken()
	const isUsernameAvailabilityRequest = api.endpoint === 'checkUsername'

	const headers: Record<string, string> = { fpid }
	if (token && !isUsernameAvailabilityRequest) {
		headers.Authorization = `Bearer ${token}`
	}

	const preparedArgs: FetchArgs =
		typeof args === 'string'
			? { url: args, headers }
			: {
					...args,
					headers: {
						...((args.headers as Record<string, string> | undefined) ?? {}),
						...headers,
					},
				}

	let result = await rawBaseQuery(preparedArgs, api, extraOptions)
	const isSetUsernameRequest = api.endpoint === 'setUsername'

	if (isSetUsernameRequest && !result.error) {
		completeUsernameGate()
	}

	if (token && !isUsernameAvailabilityRequest && !isSetUsernameRequest && isUsernameRequiredError(result)) {
		api.dispatch(setUsernameRequired(true))
		await waitForUsernameCompletion()
		result = await rawBaseQuery(preparedArgs, api, extraOptions)
	}

	const isLoginRequest = api.endpoint === 'login' || api.endpoint === 'googleLogin' || api.endpoint === 'metaMaskLogin'

	if (result.error) {
		if (result.error.status === 401 && !isLoginRequest) {
			api.dispatch(logout())
			showApiErrorToast('Session expired. Please login again.')
			return result
		}

		const errorMessage = extractApiErrorMessage(result?.error?.data)

		if (errorMessage) {
			showApiErrorToast(errorMessage)
		}
	} else {
		const message = extractApiMessage(result?.data)

		if (message) {
			showApiSuccessToast(message)
		}
	}

	return result
}
